/**
 * Local temperature for the sidebar card.
 *
 * Deliberately best-effort. This product is built to work with no internet at
 * all, so the weather is decoration: when it cannot be fetched the hook returns
 * null and the sidebar simply leaves that corner empty rather than showing a
 * dash or an error. A till that cannot reach the internet must not look broken.
 *
 * Open-Meteo needs no API key and no account, which is what makes it usable on
 * a machine nobody will ever log into to paste a token. The coordinates default
 * to Sialkot and can be overridden with VITE_WEATHER_LAT / VITE_WEATHER_LON.
 *
 * The last reading is cached in localStorage for an hour: a shop that goes
 * offline mid-morning keeps showing this morning's temperature rather than
 * losing it, and a reload does not cost a request.
 */

import { useState, useEffect } from 'react';
import {
    FiSun, FiCloud, FiCloudRain, FiCloudSnow, FiCloudLightning, FiCloudDrizzle, FiWind
} from 'react-icons/fi';

const CACHE_KEY = 'pos.weather';
const CACHE_TTL_MS = 60 * 60 * 1000;
const REFRESH_MS = 30 * 60 * 1000;

const LAT = Number(import.meta.env.VITE_WEATHER_LAT) || 32.4945;   // Sialkot
const LON = Number(import.meta.env.VITE_WEATHER_LON) || 74.5229;

/**
 * WMO weather codes, grouped to the handful of icons a sidebar needs.
 * https://open-meteo.com documents the full list; the distinctions beyond these
 * are not visible at 24 pixels.
 */
const describe = (code) => {
    if (code === 0) return { Icon: FiSun, tone: 'text-amber-500', description: 'Clear' };
    if (code <= 2) return { Icon: FiSun, tone: 'text-amber-400', description: 'Mostly clear' };
    if (code === 3) return { Icon: FiCloud, tone: 'text-slate-400', description: 'Overcast' };
    if (code <= 48) return { Icon: FiWind, tone: 'text-slate-400', description: 'Fog' };
    if (code <= 57) return { Icon: FiCloudDrizzle, tone: 'text-sky-500', description: 'Drizzle' };
    if (code <= 67) return { Icon: FiCloudRain, tone: 'text-sky-600', description: 'Rain' };
    if (code <= 77) return { Icon: FiCloudSnow, tone: 'text-sky-300', description: 'Snow' };
    if (code <= 82) return { Icon: FiCloudRain, tone: 'text-sky-600', description: 'Showers' };
    if (code <= 86) return { Icon: FiCloudSnow, tone: 'text-sky-300', description: 'Snow showers' };
    return { Icon: FiCloudLightning, tone: 'text-indigo-500', description: 'Thunderstorm' };
};

const readCache = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (Date.now() - cached.at > CACHE_TTL_MS) return null;
        return cached;
    } catch {
        return null;
    }
};

const useWeather = () => {
    const [reading, setReading] = useState(() => readCache());

    useEffect(() => {
        let cancelled = false;

        const fetchWeather = async () => {
            // A short timeout: on a bad connection this must not hold a socket
            // open behind the requests that actually matter.
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 6000);

            try {
                const url = `https://api.open-meteo.com/v1/forecast`
                    + `?latitude=${LAT}&longitude=${LON}&current=temperature_2m,weather_code`;

                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) return;

                const data = await response.json();
                const temperature = Math.round(data?.current?.temperature_2m);
                const code = data?.current?.weather_code;

                if (cancelled || !Number.isFinite(temperature)) return;

                const next = { temperature, code, at: Date.now() };
                setReading(next);
                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
                } catch {
                    // Private mode or a full quota. The reading still shows for
                    // this session; there is nothing to tell the user.
                }
            } catch {
                // Offline, blocked, or timed out. Whatever is cached stays on
                // screen; if there is nothing cached the sidebar shows nothing.
            } finally {
                clearTimeout(timer);
            }
        };

        // Skip the request entirely when a fresh reading is already cached.
        const cached = readCache();
        if (!cached) fetchWeather();

        const interval = setInterval(fetchWeather, REFRESH_MS);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);

    if (!reading) return null;
    return { temperature: reading.temperature, ...describe(reading.code) };
};

export default useWeather;
